import { authFetch } from '../../utils/cloudFunctionsAuth';
import type { EmailDraft } from '../../services/draftService';

// Transport contract only. All date, phase and money calculations live in the
// backend recurring-payment-schedule service, shared by preview and PDF.
export type ScheduleHorizon = '12' | '24' | 'term';
export interface ScheduleRow {
  id: string; date: string; amountCents: number; refundedCents: number;
  status: 'paid' | 'refunded' | 'projected'; detail: string;
  invoiceId: string | null; invoiceNumber: string | null;
}
export interface PaymentSchedule {
  subscriptionId: string; customerId: string | null; customerName: string; customerEmail: string;
  description: string; reference: string; currency: string; asOf: string;
  horizon: ScheduleHorizon; scope: string; term: string; cadence: string;
  endsAt: string | null; initialTermEndsAt: string | null;
  estimatedDates: boolean; testMode: boolean; canShare: boolean;
  issues: { code: string; message: string; blocking: boolean }[];
  notes: string[]; paid: ScheduleRow[]; upcoming: ScheduleRow[];
  receivedCents: number; refundedCents: number; projectedCents: number;
}
export interface ScheduleAttachment { filename: string; content: string; contentType: string }

async function request(body: Record<string, unknown>) {
  const res = await authFetch('https://us-central1-web-construct-app.cloudfunctions.net/recurringPaymentScheduleHttp', {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.ok) throw new Error(data.error || 'Could not prepare the payment schedule.');
  return data;
}
export async function fetchPaymentSchedule(subscriptionId: string, horizon: ScheduleHorizon): Promise<{ schedule: PaymentSchedule; revision: string }> {
  return request({ subscriptionId, horizon });
}
export async function fetchSchedulePdf(subscriptionId: string, horizon: ScheduleHorizon, revision: string): Promise<ScheduleAttachment> {
  const data = await request({ subscriptionId, horizon, revision, format: 'pdf' });
  return { filename: data.filename, content: data.content, contentType: data.contentType };
}
const escapeHtml = (s: string) => s.replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
export function scheduleEmailDraft(s: PaymentSchedule, attachment: ScheduleAttachment): Partial<EmailDraft> {
  const paragraphs = [
    'Hello,',
    `Please find the payment schedule for ${s.description} attached for your records. It includes payments already received and ${s.scope.toLowerCase()}.`,
    'Future payments are projected from the saved plan and may change if the arrangement changes. Your tax invoices will continue to be generated separately as payments are received.',
    ...(s.estimatedDates ? ['The projected dates are estimates based on the saved billing dates. Please refer to each invoice for the confirmed payment details.'] : []),
    'Please let us know if you need anything else.',
  ];
  return { subject: `Payment schedule — ${s.customerName}`, to: /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.customerEmail) ? [s.customerEmail] : [],
    htmlBody: paragraphs.map(p => `<p>${escapeHtml(p)}</p>`).join(''), textBody: paragraphs.join('\n\n'),
    attachments: [attachment], status: 'draft', source: 'portal', isReply: false, isForward: false,
    // No linkedInvoiceId: sending a schedule must not mark any invoice as sent.
  };
}
